/* eslint-disable @typescript-eslint/no-unused-vars */
// src/features/auth/state/auth-state/authSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { isTokenExpired } from '@/features/auth/utils/token.utils';
import { fetchSession, fetchSessionOutcome, getCsrf, csrfHeaders, purgeLegacyAuth } from '@/lib/session';
import { normalizeRoles, permissionsForRoles } from '@/rbac/rbac.generated';
import { clearCachedProfile } from '@/lib/hrApi';
import { deriveCoarseRole } from '../../utils/coarseRole';
import type { RootState } from '@/app/store';
import type { 
  User, 
  LoginCredentials, 
  Session,
  WorkspaceInfo,
  Permission,
  AuthError,
  MFASetupResponse,
  MFAVerifyRequest,
} from '../../types/auth.types';
import { MFAMethod } from '../../types/auth.types';

// Import from the updated authTypes (including AuthState)
import type {
  AuthState,
  AuthTokens,
  RegisterData,
  MFAChallengeState,
  MFADeviceTrust,
} from './authTypes';

// Initial state with MFA support
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  session: null,
  tokens: null,
  mfaRequired: false,
  mfaSessionToken: null,
  workspace: null,
  availableWorkspaces: [],
  permissions: [],
  error: null,
  redirectUrl: null,
  // New MFA-related state
  mfaChallenge: null,
  mfaEnabled: false,
  deviceTrusted: false,
  trustedDevices: [],
  backupCodesRemaining: 0,
};

// MFA-related async thunks
export const setupMFAThunk = createAsyncThunk<
  MFASetupResponse,
  { method: MFAMethod },
  { rejectValue: AuthError }
>(
  'auth/setupMFA',
  async ({ method }, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/mfa/setup', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...csrfHeaders(),
        },
        body: JSON.stringify({ method }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'MFA_SETUP_FAILED',
        message: 'Failed to setup MFA',
      });
    }
  }
);

export const verifyMFAThunk = createAsyncThunk<
  { user: User; tokens: AuthTokens; session: Session; deviceTrust?: MFADeviceTrust },
  MFAVerifyRequest,
  { rejectValue: AuthError }
>(
  'auth/verifyMFA',
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const sessionToken = state.auth.mfaSessionToken || state.auth.mfaChallenge?.sessionToken;
      
      const response = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-MFA-Session': sessionToken || '',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue({
        code: 'MFA_VERIFICATION_FAILED',
        message: 'MFA verification failed',
      });
    }
  }
);

export const disableMFAThunk = createAsyncThunk<
  void,
  { password: string },
  { rejectValue: AuthError }
>(
  'auth/disableMFA',
  async ({ password }, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/mfa/disable', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...csrfHeaders(),
        },
        body: JSON.stringify({ password }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
    } catch {
      return rejectWithValue({
        code: 'MFA_DISABLE_FAILED',
        message: 'Failed to disable MFA',
      });
    }
  }
);

export const generateBackupCodesThunk = createAsyncThunk<
  { backupCodes: string[]; generatedAt: string },
  void,
  { rejectValue: AuthError }
>(
  'auth/generateBackupCodes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/mfa/backup-codes', {
        method: 'POST',
        headers: { 
          ...csrfHeaders(),
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'BACKUP_CODES_GENERATION_FAILED',
        message: 'Failed to generate backup codes',
      });
    }
  }
);

export const trustDeviceThunk = createAsyncThunk<
  MFADeviceTrust,
  { deviceName?: string; duration?: number },
  { rejectValue: AuthError }
>(
  'auth/trustDevice',
  async ({ deviceName, duration = 30 }, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/devices/trust', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...csrfHeaders(),
        },
        body: JSON.stringify({ deviceName, duration }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'DEVICE_TRUST_FAILED',
        message: 'Failed to trust device',
      });
    }
  }
);

export const getTrustedDevicesThunk = createAsyncThunk<
  MFADeviceTrust[],
  void,
  { rejectValue: AuthError }
>(
  'auth/getTrustedDevices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/devices/trusted', {
        headers: { 
          ...csrfHeaders(),
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'FETCH_DEVICES_FAILED',
        message: 'Failed to fetch trusted devices',
      });
    }
  }
);

// Updated async thunks
export const loginThunk = createAsyncThunk<
  { 
    user: User; 
    tokens: AuthTokens; 
    session: Session; 
    requiresMFA?: boolean; 
    mfaChallenge?: MFAChallengeState;
    deviceTrust?: MFADeviceTrust;
  },
  LoginCredentials,
  { rejectValue: AuthError }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'NETWORK_ERROR',
        message: 'Network error occurred',
      });
    }
  }
);

// Existing thunks remain the same...
export const registerThunk = createAsyncThunk<
  { user: User; tokens: AuthTokens; workspace: WorkspaceInfo },
  RegisterData,
  { rejectValue: AuthError }
>(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch {
      return rejectWithValue({
        code: 'NETWORK_ERROR',
        message: 'Network error occurred',
      });
    }
  }
);

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await fetch('/api/auth/logout', { 
        method: 'POST',
        headers: {
          ...csrfHeaders(),
        },
      });
    } catch {
      // Logout should succeed even if API call fails
    }
  }
);

export const refreshTokenThunk = createAsyncThunk<
  { tokens: AuthTokens },
  void,
  { rejectValue: AuthError }
>(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const refreshToken = state.auth.tokens?.refreshToken;
      
      if (!refreshToken) {
        return rejectWithValue({
          code: 'NO_REFRESH_TOKEN',
          message: 'No refresh token available',
        });
      }
      
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        code: 'TOKEN_REFRESH_FAILED',
        message: 'Failed to refresh token',
      });
    }
  }
);

export const getCurrentUserThunk = createAsyncThunk<
  User & { mfaEnabled?: boolean; backupCodesRemaining?: number },
  void,
  { rejectValue: AuthError }
>(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          ...csrfHeaders(),
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        return rejectWithValue(error);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue({
        code: 'FETCH_USER_FAILED',
        message: 'Failed to fetch user data',
      });
    }
  }
);

/** Decode the `roles` claim from a JWT (base64url payload). Empty on any error. */
function decodeJwtRoles(token: string): string[] {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    const r = payload?.roles;
    return Array.isArray(r) ? r : r ? [r] : [];
  } catch {
    return [];
  }
}

export const initializeAuthThunk = createAsyncThunk<
  { 
    user: User & { mfaEnabled?: boolean }; 
    workspace: WorkspaceInfo;
    deviceTrust?: MFADeviceTrust;
  } | null,
  void,
  { rejectValue: AuthError }
>(
  'auth/initialize',
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      // P2-8: restore the session from the httpOnly cookie via the gateway
      // (GET /api/v1/auth/session). This also captures the session-bound CSRF
      // token for every subsequent write. No token is read from JS.
      //
      // The SESSION is the identity authority. The persisted store is only a
      // render hint: the httpOnly cookie is shared by every KaziPeople app on
      // this API host, so another login (admin portal, second account) replaces
      // it underneath an open tab. Rendering the persisted user while requests
      // authenticate as someone else caused the "profile not found for a user
      // the UI says is logged in" split-brain — reconcile on every hydration.
      const outcome = await fetchSessionOutcome();
      if (outcome.status === 'error') {
        // Transient (network/5xx): indeterminate — keep the current auth state.
        // Logging the user out on a blip was itself a regression source.
        return null;
      }
      if (outcome.status === 'unauthenticated') {
        // No valid cookie session — clear any stale persisted auth → login page.
        purgeLegacyAuth();
        clearCachedProfile();
        dispatch(clearAuth());
        return null;
      }
      const session = outcome.user;
      if (!session.id) return null; // malformed session payload: keep state
      try {
        const persistedId = (getState() as RootState).auth.user?.id ?? null;
        if (persistedId && persistedId !== session.id) {
          // Different principal than the one this tab was showing: drop every
          // per-identity cache before the new identity renders.
          clearCachedProfile();
          try {
            localStorage.setItem('userId', session.id);
            if (session.email) localStorage.setItem('userEmail', session.email);
            localStorage.removeItem('userName');
          } catch { /* storage unavailable */ }
        }
        const serverRoles = normalizeRoles(Array.isArray(session.roles) ? session.roles : []);
        dispatch(syncSessionIdentity({
          id: session.id,
          email: session.email,
          roles: serverRoles,
          role: deriveCoarseRole(serverRoles),
        }));
        // Prefer the server-resolved permission set from the session (includes
        // dynamic custom-role grants); fall back to the static role map.
        const perms = Array.isArray(session.permissions) && session.permissions.length > 0
          ? session.permissions
          : permissionsForRoles(serverRoles);
        dispatch(setPermissions(perms as any));
      } catch {
        /* keep the session on any error */
      }
      return null;
    } catch (error) {
      return rejectWithValue({
        code: 'INITIALIZATION_FAILED',
        message: 'Failed to initialize auth',
      });
    }
  }
);

// Slice with MFA support
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isInitialized = true; // logging in means the auth system is settled
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    // Reconcile the store with the verified cookie session (identity authority).
    // Same id → sync roles/role; different id (or no persisted user) → ADOPT the
    // session identity so the UI can never keep rendering a previous principal
    // while requests authenticate as the session's one. `roles: []` from a
    // still-hydrating session never wipes known roles for the SAME user.
    syncSessionIdentity: (
      state,
      action: PayloadAction<{ id: string; email: string | null; roles: string[]; role: User['role'] }>,
    ) => {
      const s = action.payload;
      if (!state.user || state.user.id !== s.id) {
        state.user = {
          id: s.id,
          email: s.email || '',
          name: '', // filled by the profile self-heal fetch
          firstName: '',
          lastName: '',
          role: s.role,
          roles: s.roles,
        } as unknown as User;
      } else {
        if (s.roles.length > 0) {
          state.user = { ...state.user, roles: s.roles, role: s.role } as User;
        }
        if (s.email) state.user.email = s.email;
      }
      state.isAuthenticated = true;
      state.isInitialized = true;
    },
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      // P2-8: tokens live in the httpOnly cookie now — do NOT persist them to
      // localStorage. Keep them in Redux state only for any in-memory reader.
      state.tokens = action.payload;
      state.isInitialized = true;
    },
    clearAuth: () => {
      purgeLegacyAuth(); // remove any pre-migration localStorage tokens
      clearCachedProfile(); // per-identity profile cache must not survive auth loss
      localStorage.removeItem('deviceTrustToken');
      // Stay "initialized" — being logged out is a settled state, not "checking".
      return { ...initialState, isInitialized: true };
    },
    // Updated MFA reducers
    setMFARequired: (state, action: PayloadAction<MFAChallengeState | boolean>) => {
      if (typeof action.payload === 'boolean') {
        state.mfaRequired = action.payload;
        if (!action.payload) {
          state.mfaChallenge = null;
          state.mfaSessionToken = null;
        }
      } else {
        state.mfaRequired = true;
        state.mfaChallenge = action.payload;
        state.mfaSessionToken = action.payload.sessionToken || null;
      }
    },
    setMFASetup: (state, action: PayloadAction<boolean>) => {
      state.mfaEnabled = action.payload;
      if (state.user) {
        state.user = {
          ...state.user,
          metadata: {
            ...state.user.metadata,
            mfaEnabled: action.payload,
          },
        };
      }
    },
    setDeviceTrusted: (state, action: PayloadAction<boolean>) => {
      state.deviceTrusted = action.payload;
    },
    clearMFAChallenge: (state) => {
      state.mfaChallenge = null;
      state.mfaRequired = false;
      state.mfaSessionToken = null;
    },
    setBackupCodesRemaining: (state, action: PayloadAction<number>) => {
      state.backupCodesRemaining = action.payload;
    },
    addTrustedDevice: (state, action: PayloadAction<MFADeviceTrust>) => {
      state.trustedDevices.push(action.payload);
    },
    removeTrustedDevice: (state, action: PayloadAction<string>) => {
      state.trustedDevices = state.trustedDevices.filter(
        device => device.deviceId !== action.payload
      );
    },
    setTrustedDevices: (state, action: PayloadAction<MFADeviceTrust[]>) => {
      state.trustedDevices = action.payload;
    },
    // Existing reducers
    setWorkspace: (state, action: PayloadAction<WorkspaceInfo>) => {
      state.workspace = action.payload;
    },
    setPermissions: (state, action: PayloadAction<Permission[]>) => {
      state.permissions = action.payload;
    },
    setRedirectUrl: (state, action: PayloadAction<string | null>) => {
      state.redirectUrl = action.payload;
    },
    setError: (state, action: PayloadAction<AuthError | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Additional actions for compatibility
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; tokens: AuthTokens; session?: Session }>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.session = action.payload.session || null;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<AuthError>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    logoutAction: () => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('deviceTrustToken');
      return { ...initialState, isInitialized: true };
    },
  },
  extraReducers: (builder) => {
    // Login with MFA support
    builder
      .addCase(loginThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true; // Mark as initialized after login

        if (action.payload.requiresMFA) {
          // MFA required - set challenge state
          state.mfaRequired = true;
          if (action.payload.mfaChallenge) {
            state.mfaChallenge = action.payload.mfaChallenge;
            state.mfaSessionToken = action.payload.mfaChallenge.sessionToken || null;
          } else {
            state.mfaChallenge = {
              required: true,
              sessionToken: undefined,
              availableMethods: [MFAMethod.TOTP],
              attemptsRemaining: 3,
            };
            state.mfaSessionToken = null;
          }
        } else {
          // Login successful
          state.user = action.payload.user;
          state.tokens = action.payload.tokens;
          state.session = action.payload.session;
          state.isAuthenticated = true;
          state.mfaRequired = false;
          state.mfaSessionToken = null;
          state.mfaChallenge = null;

          if (action.payload.deviceTrust) {
            state.deviceTrusted = true;
            state.trustedDevices = [action.payload.deviceTrust];
          }

          // Store tokens
        }
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          code: 'LOGIN_FAILED',
          message: 'Login failed',
        };
      });

    // MFA Setup
    builder
      .addCase(setupMFAThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setupMFAThunk.fulfilled, (state) => {
        state.isLoading = false;
        // The actual MFA enabling happens after verification
      })
      .addCase(setupMFAThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          code: 'MFA_SETUP_FAILED',
          message: 'Failed to setup MFA',
        };
      });

    // MFA Verification
    builder
      .addCase(verifyMFAThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyMFAThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true; // Mark as initialized after MFA verification
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.session = action.payload.session;
        state.isAuthenticated = true;
        state.mfaRequired = false;
        state.mfaSessionToken = null;
        state.mfaChallenge = null;
        state.mfaEnabled = true;

        if (action.payload.deviceTrust) {
          state.deviceTrusted = true;
          state.trustedDevices = [action.payload.deviceTrust];
        }

      })
      .addCase(verifyMFAThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          code: 'MFA_VERIFICATION_FAILED',
          message: 'MFA verification failed',
        };
        
        // Update attempts remaining if provided
        if (state.mfaChallenge && action.payload?.details?.attemptsRemaining !== undefined) {
          state.mfaChallenge.attemptsRemaining = action.payload.details.attemptsRemaining as number;
        }
      });

    // Disable MFA
    builder
      .addCase(disableMFAThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(disableMFAThunk.fulfilled, (state) => {
        state.isLoading = false;
        state.mfaEnabled = false;
        state.backupCodesRemaining = 0;
        if (state.user) {
          state.user = {
            ...state.user,
            metadata: {
              ...state.user.metadata,
              mfaEnabled: false,
            },
          };
        }
      })
      .addCase(disableMFAThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          code: 'MFA_DISABLE_FAILED',
          message: 'Failed to disable MFA',
        };
      });

    // Generate Backup Codes
    builder
      .addCase(generateBackupCodesThunk.fulfilled, (state, action) => {
        // Update backup codes count
        state.backupCodesRemaining = action.payload.backupCodes.length;
      });

    // Trust Device
    builder
      .addCase(trustDeviceThunk.fulfilled, (state, action) => {
        state.deviceTrusted = true;
        state.trustedDevices.push(action.payload);
      });

    // Get Trusted Devices
    builder
      .addCase(getTrustedDevicesThunk.fulfilled, (state, action) => {
        state.trustedDevices = action.payload;
        // Check if current device is trusted
        const currentDeviceId = localStorage.getItem('deviceId');
        state.deviceTrusted = action.payload.some(
          device => device.deviceId === currentDeviceId
        );
      });

    // Register
    builder
      .addCase(registerThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true; // Mark as initialized after registration
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.workspace = action.payload.workspace;
        state.isAuthenticated = true;

      })
      .addCase(registerThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || {
          code: 'REGISTRATION_FAILED',
          message: 'Registration failed',
        };
      });

    // Logout
    builder
      .addCase(logoutThunk.fulfilled, () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('deviceTrustToken');
        return initialState;
      });

    // Refresh token
    builder
      .addCase(refreshTokenThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(refreshTokenThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tokens = action.payload.tokens;
        
      })
      .addCase(refreshTokenThunk.rejected, (state) => {
        state.isLoading = false;
        // If refresh fails, clear auth state
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        return initialState;
      });

    // Get current user with MFA info
    builder
      .addCase(getCurrentUserThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUserThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.mfaEnabled = action.payload.mfaEnabled || false;
        state.backupCodesRemaining = action.payload.backupCodesRemaining || 0;
      })
      .addCase(getCurrentUserThunk.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
      });

    // Initialize auth with MFA info
    builder
      .addCase(initializeAuthThunk.pending, (state) => {
        // Block the UI only on the FIRST initialization (cold boot). Later
        // dispatches are background revalidations (tab-focus role rechecks) —
        // flipping isLoading here would swap the whole page for the
        // "Checking authentication..." screen mid-session.
        if (!state.isInitialized) state.isLoading = true;
      })
      .addCase(initializeAuthThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isInitialized = true;
        
        if (action.payload) {
          state.user = action.payload.user;
          state.workspace = action.payload.workspace;
          state.isAuthenticated = true;
          state.mfaEnabled = action.payload.user.mfaEnabled || false;
          
          if (action.payload.deviceTrust) {
            state.deviceTrusted = true;
            state.trustedDevices = [action.payload.deviceTrust];
          }
        }
      })
      .addCase(initializeAuthThunk.rejected, (state) => {
        state.isLoading = false;
        state.isInitialized = true;
      });
  },
});

// Actions
export const {
  setUser,
  updateUser,
  syncSessionIdentity,
  setTokens,
  clearAuth,
  setMFARequired,
  setMFASetup,
  setDeviceTrusted,
  clearMFAChallenge,
  setBackupCodesRemaining,
  addTrustedDevice,
  removeTrustedDevice,
  setTrustedDevices,
  setWorkspace,
  setPermissions,
  setRedirectUrl,
  setError,
  clearError,
  loginStart,
  loginSuccess,
  loginFailure,
  logoutAction,
} = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsInitialized = (state: RootState) => state.auth.isInitialized;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectMFARequired = (state: RootState) => state.auth.mfaRequired;
export const selectWorkspace = (state: RootState) => state.auth.workspace;
export const selectPermissions = (state: RootState) => state.auth.permissions;

// New MFA selectors
export const selectMFAChallenge = (state: RootState) => state.auth.mfaChallenge;
export const selectIsMFAEnabled = (state: RootState) => state.auth.mfaEnabled;
export const selectIsDeviceTrusted = (state: RootState) => state.auth.deviceTrusted;
export const selectTrustedDevices = (state: RootState) => state.auth.trustedDevices;
export const selectBackupCodesRemaining = (state: RootState) => state.auth.backupCodesRemaining;
export const selectMFASessionToken = (state: RootState) => state.auth.mfaSessionToken;

// Export reducer
export const authReducer = authSlice.reducer;
export default authSlice.reducer;